import { curSym } from "@/lib/company";
import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Trophy, Award, Gift, Plus, Minus, Pencil, Trash2, Loader2, Crown, Medal,
  TrendingUp, TrendingDown, Sparkles, History, Settings2, Clock, Target, AlertTriangle, Star,
} from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const REWARD_EMPTY = { name: "", description: "", points_required: 0, active: true };
const RULES_EMPTY = { late_shift_enabled: false, shift_open_deadline: "09:15", late_shift_penalty: 5, discrepancy_enabled: false, discrepancy_penalty: 5, target_enabled: false, daily_sales_target: 0, target_bonus: 5, survey_praise_enabled: false, survey_praise_min: 5, survey_praise_bonus: 3, survey_low_enabled: false, survey_low_max: 2, survey_low_penalty: 3, receipt_clean_enabled: false, receipt_clean_bonus: 2, receipt_correction_enabled: false, receipt_correction_penalty: 3, cash_reward_enabled: false, cash_reward_exact: 5, cash_reward_over: 7, cash_require_activity: true, cash_min_open_hours: 0, cash_min_sales_count: 0, cash_min_sales_amount: 0, cash_below_points: 0, survey_location_enabled: false, survey_location_min_unique: 20, survey_location_min_avg: 4.0, survey_location_bonus: 5 };

const AWARD_ROLES = [["admin", "Yönetici"], ["assistant", "Asistan"], ["store_manager", "Mağaza Müdürü"]];

const rankBadge = (rank) => {
  if (rank === 1) return <Crown className="h-4 w-4 text-amber-500" />;
  if (rank === 2) return <Medal className="h-4 w-4 text-slate-400" />;
  if (rank === 3) return <Medal className="h-4 w-4 text-amber-700" />;
  return <span className="inline-block w-4 text-center text-xs font-bold text-slate-400">{rank}</span>;
};

const scoreColor = (s) => (s >= 110 ? "text-emerald-600" : s >= 90 ? "text-slate-700" : s >= 60 ? "text-amber-600" : "text-red-600");

export default function Scoring() {
  const { can } = useAuth();
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [summary, setSummary] = useState({ leaderboard: [], recent: [], base: 100, can_manage: false });
  const [rewards, setRewards] = useState([]);
  const [grants, setGrants] = useState([]);
  const [eom, setEom] = useState(null);
  const [trend, setTrend] = useState([]);
  const [eomRewardId, setEomRewardId] = useState("");
  const [cashBoard, setCashBoard] = useState(null);
  const [cashCfg, setCashCfg] = useState(null);
  const [savingCash, setSavingCash] = useState(false);
  const [runningCash, setRunningCash] = useState(false);
  const [loading, setLoading] = useState(true);

  // manual adjust dialog
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjUser, setAdjUser] = useState("");
  const [adjPoints, setAdjPoints] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [saving, setSaving] = useState(false);

  // reward dialog
  const [rwOpen, setRwOpen] = useState(false);
  const [rwEditing, setRwEditing] = useState(null);
  const [rwForm, setRwForm] = useState(REWARD_EMPTY);

  // grant dialog
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantReward, setGrantReward] = useState(null);
  const [grantUser, setGrantUser] = useState("");
  const [grantNote, setGrantNote] = useState("");

  // scoring automation rules
  const [rules, setRules] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesForm, setRulesForm] = useState(RULES_EMPTY);

  const canManage = can("score.manage");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/scoring/summary?period=${period}`).then((r) => setSummary(r.data)).catch(() => {}),
      api.get(`/scoring/rewards`).then((r) => setRewards(r.data)).catch(() => {}),
      api.get(`/scoring/rewards/grants?period=${period}`).then((r) => setGrants(r.data)).catch(() => {}),
      api.get(`/scoring/rules`).then((r) => setRules(r.data)).catch(() => {}),
      api.get(`/scoring/employee-of-month`).then((r) => setEom(r.data)).catch(() => {}),
      api.get(`/scoring/trend?months=6`).then((r) => setTrend(r.data.months || [])).catch(() => {}),
      api.get(`/scoring/eom-reward`).then((r) => setEomRewardId(r.data.reward_id || "")).catch(() => {}),
      api.get(`/scoring/cash-accuracy?period=${period}`).then((r) => setCashBoard(r.data)).catch(() => {}),
      api.get(`/scoring/cash-award-config`).then((r) => setCashCfg(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const board = summary.leaderboard || [];

  const saveCashCfg = async () => {
    setSavingCash(true);
    try {
      const { data } = await api.put("/scoring/cash-award-config", { ...cashCfg, badge_points: Number(cashCfg.badge_points) || 0 });
      setCashCfg(data); toast.success("Kasa ödül ayarı kaydedildi");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingCash(false); }
  };

  const runCashAward = async () => {
    setRunningCash(true);
    try {
      const { data } = await api.post("/scoring/cash-accuracy/run-award");
      if (data.winner) toast.success(`Birinci: ${data.winner.name} (★${data.winner.points}) · ${(data.notified || []).length} kişiye prim bildirimi gitti`);
      else toast.info("Geçen ay kasa doğruluğu ödülü kazanan yok");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setRunningCash(false); }
  };

  const submitAdjust = async () => {
    if (!adjUser) return toast.error("Personel seçin");
    const pts = parseInt(adjPoints, 10);
    if (!pts) return toast.error("Puan girin (+ veya -)");
    if (adjReason.trim().length < 3) return toast.error("Gerekçe zorunlu (en az 3 karakter)");
    setSaving(true);
    try {
      await api.post("/scoring/adjust", { user_email: adjUser, points: pts, reason: adjReason.trim() });
      toast.success(`Puan güncellendi (${pts > 0 ? "+" : ""}${pts})`);
      setAdjOpen(false); setAdjUser(""); setAdjPoints(""); setAdjReason(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openReward = (rw) => {
    setRwEditing(rw || null);
    setRwForm(rw ? { name: rw.name, description: rw.description || "", points_required: rw.points_required || 0, active: rw.active !== false } : REWARD_EMPTY);
    setRwOpen(true);
  };
  const submitReward = async () => {
    if (!rwForm.name.trim()) return toast.error("Ödül adı zorunlu");
    setSaving(true);
    const body = { ...rwForm, name: rwForm.name.trim(), points_required: Number(rwForm.points_required) || 0 };
    try {
      if (rwEditing) await api.put(`/scoring/rewards/${rwEditing.id}`, body);
      else await api.post("/scoring/rewards", body);
      toast.success(rwEditing ? "Ödül güncellendi" : "Ödül eklendi");
      setRwOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const delReward = async (rw) => {
    if (!window.confirm(`"${rw.name}" ödülü silinsin mi?`)) return;
    try { await api.delete(`/scoring/rewards/${rw.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openGrant = (rw) => { setGrantReward(rw); setGrantUser(""); setGrantNote(""); setGrantOpen(true); };
  const submitGrant = async () => {
    if (!grantUser) return toast.error("Personel seçin");
    setSaving(true);
    try {
      await api.post(`/scoring/rewards/${grantReward.id}/grant`, { user_email: grantUser, note: grantNote.trim() });
      toast.success("Ödül verildi 🏆");
      setGrantOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const evLabel = (e) => {
    if (e.type === "waste_penalty") return `Fire/Zayiat: ${e.product_name || "-"}${e.quantity ? ` (${e.quantity})` : ""}`;
    if (e.type === "manual") return `Manuel: ${e.reason || "-"}`;
    if (e.type === "late_shift") return e.reason || "Geç kasa açılışı";
    if (e.type === "discrepancy") return e.reason || "Teslim/sayım tutarsızlığı";
    if (e.type === "target_hit") return e.reason || "Günlük hedef aşıldı";
    if (e.type === "survey_praise") return e.reason || "Yüksek müşteri anket puanı";
    if (e.type === "survey_low") return e.reason || "Düşük müşteri anket puanı";
    if (e.type === "receipt_clean") return e.reason || "Temiz / eksiksiz mal kabulü";
    if (e.type === "receipt_correction") return e.reason || "Mal kabul düzeltme talebi";
    if (e.type === "survey_location") return e.reason || "Günlük müşteri memnuniyeti hedefi";
    if (e.type === "cash_accuracy") return e.reason || "Kasa sayım doğruluğu";
    if (e.type === "on_time_open") return e.reason || "Kasa zamanında açıldı";
    if (e.type === "cash_champion") return e.reason || "Ayın Kasa Doğruluğu Birincisi";
    return e.reason || e.type || "-";
  };

  const saveEomReward = async (rid) => {
    setEomRewardId(rid);
    try { await api.put("/scoring/eom-reward", { reward_id: rid || null }); toast.success("Ayın Personeli ödülü ayarlandı"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const submitRules = async () => {
    setSaving(true);
    try {
      const body = {
        ...rulesForm,
        late_shift_penalty: Number(rulesForm.late_shift_penalty) || 0,
        discrepancy_penalty: Number(rulesForm.discrepancy_penalty) || 0,
        daily_sales_target: Number(rulesForm.daily_sales_target) || 0,
        target_bonus: Number(rulesForm.target_bonus) || 0,
        survey_praise_min: Number(rulesForm.survey_praise_min) || 0,
        survey_praise_bonus: Number(rulesForm.survey_praise_bonus) || 0,
        survey_low_max: Number(rulesForm.survey_low_max) || 0,
        survey_low_penalty: Number(rulesForm.survey_low_penalty) || 0,
        receipt_clean_bonus: Number(rulesForm.receipt_clean_bonus) || 0,
        receipt_correction_penalty: Number(rulesForm.receipt_correction_penalty) || 0,
        cash_reward_exact: Number(rulesForm.cash_reward_exact) || 0,
        cash_reward_over: Number(rulesForm.cash_reward_over) || 0,
        cash_min_open_hours: Number(rulesForm.cash_min_open_hours) || 0,
        cash_min_sales_count: Number(rulesForm.cash_min_sales_count) || 0,
        cash_min_sales_amount: Number(rulesForm.cash_min_sales_amount) || 0,
        cash_below_points: Number(rulesForm.cash_below_points) || 0,
        survey_location_min_unique: Number(rulesForm.survey_location_min_unique) || 0,
        survey_location_min_avg: Number(rulesForm.survey_location_min_avg) || 0,
        survey_location_bonus: Number(rulesForm.survey_location_bonus) || 0,
      };
      const { data } = await api.put("/scoring/rules", body);
      setRules(data); toast.success("Puan kuralları kaydedildi"); setRulesOpen(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5 fade-in" data-testid="scoring-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-black tracking-tight text-slate-900">
            <Trophy className="h-6 w-6 text-amber-500" /> Puan & Ödül Sistemi
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Herkes {summary.base || 100} puanla başlar. Fire/zayiat gibi olaylar puanı düşürür, başarılar artırır.
            Liste <b>ay bazında</b>dır ve her ay başında sıfırlanır.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[11px] text-slate-500">Dönem (Ay)</Label>
            <Input data-testid="scoring-period" type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1 h-10 w-40" />
          </div>
          {canManage && (
            <Button data-testid="scoring-rules-btn" onClick={() => { setRulesForm(rules || RULES_EMPTY); setRulesOpen(true); }} variant="outline" className="h-10 border-slate-300">
              <Settings2 className="mr-1.5 h-4 w-4" /> Kurallar
            </Button>
          )}
          {canManage && (
            <Button data-testid="scoring-adjust-btn" onClick={() => setAdjOpen(true)} className="h-10 bg-brand-green text-white hover:bg-brand-green-600">
              <Sparkles className="mr-1.5 h-4 w-4" /> Puan Ekle / Çıkar
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand-green" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Employee of the Month */}
          <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 shadow-sm" data-testid="scoring-eom">
            <p className="overline mb-3 flex items-center gap-1.5"><Crown className="h-3.5 w-3.5 text-amber-500" /> Ayın Personeli · {eom?.period || "—"}</p>
            {(() => {
              const win = eom?.badge || eom?.live;
              if (!win || !win.user_email) return <p className="text-sm text-slate-400">Henüz veri yok.</p>;
              return (
                <div className="flex items-center gap-4" data-testid="eom-winner">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-amber-400 text-white shadow-inner">
                    <Crown className="h-8 w-8" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate font-heading text-lg font-black text-slate-900">{win.name}</p>
                    <p className="text-xs text-slate-500">{win.role_label || win.role}</p>
                    <p className="mt-1 flex items-center gap-1.5 text-sm font-bold text-amber-600">
                      <Trophy className="h-4 w-4" /> {win.score} puan
                      {eom?.badge ? <span className="ml-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white">ROZET</span>
                        : <span className="ml-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-500">canlı</span>}
                    </p>
                  </div>
                </div>
              );
            })()}
            {canManage && (
              <div className="mt-3 border-t border-amber-200/70 pt-3">
                <label className="text-[11px] font-medium text-slate-500">Otomatik verilecek ödül (ay başında)</label>
                <select data-testid="eom-reward-select" value={eomRewardId} onChange={(e) => saveEomReward(e.target.value)}
                  className="mt-1 h-9 w-full rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                  <option value="">Otomatik (en yüksek uygun ödül)</option>
                  {rewards.filter((r) => r.active !== false).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Monthly trend */}
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="scoring-trend">
            <p className="overline mb-3 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-emerald-600" /> Aylık Puan Trendi (son 6 ay)</p>
            {trend.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">Trend verisi yok.</p>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={trend} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="bonus" name="Ödül (+)" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="penalty" name="Ceza (−)" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="net" name="Net" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Leaderboard */}
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="scoring-leaderboard">
            <p className="overline mb-3 flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5 text-amber-500" /> Puan Tablosu · {period}</p>
            <div className="overflow-hidden rounded-lg ring-1 ring-inset ring-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 w-10">#</th>
                    <th className="px-3 py-2">Personel</th>
                    <th className="px-3 py-2 hidden sm:table-cell">Rol</th>
                    <th className="px-3 py-2 text-right">Değişim</th>
                    <th className="px-3 py-2 text-right">Puan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {board.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Kayıt yok.</td></tr>}
                  {board.map((b) => (
                    <tr key={b.user_email} data-testid={`scoring-row-${b.user_email}`} className="hover:bg-slate-50/70">
                      <td className="px-3 py-2">{rankBadge(b.rank)}</td>
                      <td className="px-3 py-2">
                        <p className="font-semibold text-slate-800">{b.name}</p>
                        <p className="text-[10px] text-slate-400">{b.location_name || "—"} · {b.events} olay</p>
                      </td>
                      <td className="px-3 py-2 hidden sm:table-cell text-xs text-slate-500">{b.role_label}</td>
                      <td className="px-3 py-2 text-right">
                        {b.delta === 0 ? <span className="text-slate-400">0</span> : (
                          <span className={`inline-flex items-center gap-0.5 font-semibold ${b.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>
                            {b.delta > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                            {b.delta > 0 ? "+" : ""}{b.delta}
                          </span>
                        )}
                      </td>
                      <td className={`px-3 py-2 text-right tabular text-lg font-black ${scoreColor(b.score)}`} data-testid={`scoring-score-${b.user_email}`}>{b.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Kasa Doğruluğu Ödül Sıralaması */}
          <div className="rounded-2xl border border-amber-200 bg-white p-5 shadow-sm" data-testid="cash-accuracy-card">
            <p className="overline mb-3 flex items-center gap-1.5"><Star className="h-3.5 w-3.5 text-amber-500" /> Kasa Doğruluğu Ödülü · {period}</p>
            {(!cashBoard || (cashBoard.leaderboard || []).length === 0) ? (
              <p className="py-8 text-center text-sm text-slate-400">Bu ay kasa denk/fazla çıkaran kasiyer ödülü yok.<br /><span className="text-[11px]">Kural: Puan &amp; Ödül &gt; Kurallar &gt; “Kasa sayım doğruluğu”.</span></p>
            ) : (
              <div className="space-y-2" data-testid="cash-accuracy-list">
                {(cashBoard.leaderboard || []).slice(0, 8).map((b) => (
                  <div key={b.user_email} data-testid={`cash-accuracy-row-${b.user_email}`} className="flex items-center justify-between rounded-lg bg-amber-50/60 px-3 py-2 ring-1 ring-inset ring-amber-100">
                    <div className="flex min-w-0 items-center gap-2">
                      {rankBadge(b.rank)}
                      <div className="min-w-0"><p className="truncate font-semibold text-slate-800">{b.name}</p><p className="text-[10px] text-slate-400">{b.count} kez denk/fazla · {b.role_label}</p></div>
                    </div>
                    <span className="tabular font-black text-amber-600">★ +{b.points}</span>
                  </div>
                ))}
                <p className="pt-1 text-center text-[11px] text-slate-400">Toplam dağıtılan ödül: <b className="text-amber-600">★ {cashBoard.total_awarded}</b></p>
              </div>
            )}
          </div>

          {canManage && cashCfg && (
            <div className="rounded-2xl border border-amber-200 bg-white p-5 shadow-sm" data-testid="cash-award-config-card">
              <div className="flex items-center justify-between">
                <p className="overline flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5 text-amber-500" /> Ay Sonu Kasa Birincisi — Ödül & Prim Bildirimi</p>
                <Switch data-testid="cash-award-enabled" checked={cashCfg.enabled} onCheckedChange={(v) => setCashCfg({ ...cashCfg, enabled: v })} />
              </div>
              <p className="mt-1 text-[11px] text-slate-400">Her ay başı otomatik: birinciye rozet puanı + tebrik; seçtiğiniz yöneticilere "prim önerisi" bildirimi gider.</p>
              <div className="mt-3 space-y-3">
                <div><Label className="text-[11px]">Birinciye rozet puanı (+)</Label><Input data-testid="cash-award-points" type="number" value={cashCfg.badge_points} onChange={(e) => setCashCfg({ ...cashCfg, badge_points: e.target.value })} className="mt-1 h-9 w-32 tabular" /></div>
                <div>
                  <Label className="text-[11px]">Prim bildirimi kime gitsin? (roller)</Label>
                  <div className="mt-1 flex flex-wrap gap-2" data-testid="cash-award-roles">
                    {AWARD_ROLES.map(([rk, rl]) => {
                      const on = (cashCfg.notify_roles || []).includes(rk);
                      return (
                        <button key={rk} type="button" data-testid={`cash-award-role-${rk}`}
                          onClick={() => setCashCfg({ ...cashCfg, notify_roles: on ? cashCfg.notify_roles.filter((x) => x !== rk) : [...(cashCfg.notify_roles || []), rk] })}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${on ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{rl}</button>
                      );
                    })}
                  </div>
                </div>
                <div><Label className="text-[11px]">Ek e-posta(lar) — virgülle ayırın (opsiyonel)</Label><Input data-testid="cash-award-emails" value={(cashCfg.notify_emails || []).join(", ")} onChange={(e) => setCashCfg({ ...cashCfg, notify_emails: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="mt-1 h-9" placeholder="patron@firma.com, bolge@firma.com" /></div>
                <div><Label className="text-[11px]">Özel mesaj (opsiyonel)</Label><Input data-testid="cash-award-message" value={cashCfg.message} onChange={(e) => setCashCfg({ ...cashCfg, message: e.target.value })} className="mt-1 h-9" placeholder="Boşsa otomatik metin kullanılır" /></div>
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="cash-award-save" onClick={saveCashCfg} disabled={savingCash} size="sm" className="bg-amber-500 text-white hover:bg-amber-600">{savingCash ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Kaydet</Button>
                  <Button data-testid="cash-award-run" onClick={runCashAward} disabled={runningCash} size="sm" variant="outline">{runningCash ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Geçen Ayı Şimdi Çalıştır</Button>
                </div>
              </div>
            </div>
          )}

          {/* Rewards */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="scoring-rewards">
              <div className="mb-3 flex items-center justify-between">
                <p className="overline flex items-center gap-1.5"><Gift className="h-3.5 w-3.5 text-brand-orange" /> Ödüller</p>
                {canManage && (
                  <Button data-testid="reward-new-btn" onClick={() => openReward(null)} size="sm" variant="outline" className="h-7 border-brand-orange text-brand-orange hover:bg-orange-50">
                    <Plus className="mr-1 h-3.5 w-3.5" /> Yeni
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {rewards.length === 0 && <p className="text-sm text-slate-400">Tanımlı ödül yok.</p>}
                {rewards.map((rw) => (
                  <div key={rw.id} data-testid={`reward-card-${rw.id}`} className={`rounded-xl border p-3 ${rw.active === false ? "border-slate-200 bg-slate-50 opacity-70" : "border-amber-200 bg-amber-50/50"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="flex items-center gap-1.5 font-semibold text-slate-800"><Award className="h-4 w-4 text-amber-500" /> {rw.name}</p>
                        {rw.description && <p className="mt-0.5 text-[11px] text-slate-500">{rw.description}</p>}
                        <p className="mt-1 text-[11px] font-medium text-amber-700">≥ {rw.points_required} puan{rw.active === false ? " · pasif" : ""}</p>
                      </div>
                      {canManage && (
                        <div className="flex shrink-0 gap-1">
                          <button data-testid={`reward-edit-${rw.id}`} onClick={() => openReward(rw)} className="rounded-sm p-1.5 text-slate-500 hover:bg-white"><Pencil className="h-3.5 w-3.5" /></button>
                          <button data-testid={`reward-del-${rw.id}`} onClick={() => delReward(rw)} className="rounded-sm p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                    </div>
                    {canManage && (
                      <Button data-testid={`reward-grant-${rw.id}`} onClick={() => openGrant(rw)} size="sm" className="mt-2 h-7 w-full bg-brand-green text-white hover:bg-brand-green-600">
                        <Gift className="mr-1 h-3.5 w-3.5" /> Ödül Ver
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Granted this period */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="scoring-grants">
              <p className="overline mb-3 flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5 text-emerald-600" /> Verilen Ödüller · {period}</p>
              <div className="space-y-1.5">
                {grants.length === 0 && <p className="text-sm text-slate-400">Bu dönemde ödül verilmedi.</p>}
                {grants.map((g) => (
                  <div key={g.id} className="rounded-lg bg-emerald-50 px-3 py-2 text-sm">
                    <p className="font-semibold text-emerald-800">{g.reward_name}</p>
                    <p className="text-[11px] text-emerald-700/80">{g.user_name || g.user_email}{g.note ? ` · ${g.note}` : ""}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent events */}
          <div className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="scoring-events">
            <p className="overline mb-3 flex items-center gap-1.5"><History className="h-3.5 w-3.5 text-slate-500" /> Puan Hareketleri · {period}</p>
            <div className="max-h-80 space-y-1.5 overflow-y-auto">
              {(summary.recent || []).length === 0 && <p className="text-sm text-slate-400">Bu dönemde puan hareketi yok.</p>}
              {(summary.recent || []).map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-700">{e.user_email}</p>
                    <p className="text-[11px] text-slate-400">{evLabel(e)}{e.created_by ? ` · ${e.created_by}` : ""}</p>
                  </div>
                  <span className={`tabular font-bold ${(e.points || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{(e.points || 0) > 0 ? "+" : ""}{e.points || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Manual adjust dialog */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent data-testid="scoring-adjust-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Puan Ekle / Çıkar</DialogTitle>
            <DialogDescription>Bir personelin bu ayki puanını artırın (+) veya azaltın (−). Gerekçe zorunludur.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Personel</Label>
              <select data-testid="adjust-user" value={adjUser} onChange={(e) => setAdjUser(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                <option value="">Seçin</option>
                {board.map((b) => <option key={b.user_email} value={b.user_email}>{b.name} ({b.role_label})</option>)}
              </select>
            </div>
            <div>
              <Label>Puan (+ / −)</Label>
              <div className="mt-1 flex items-center gap-2">
                <Button type="button" variant="outline" size="icon" className="h-10 w-10" onClick={() => setAdjPoints((p) => String((parseInt(p, 10) || 0) - 5))}><Minus className="h-4 w-4" /></Button>
                <Input data-testid="adjust-points" type="number" value={adjPoints} onChange={(e) => setAdjPoints(e.target.value)} placeholder="ör. +10 / -5" className="tabular text-center" />
                <Button type="button" variant="outline" size="icon" className="h-10 w-10" onClick={() => setAdjPoints((p) => String((parseInt(p, 10) || 0) + 5))}><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
            <div>
              <Label>Gerekçe</Label>
              <Textarea data-testid="adjust-reason" value={adjReason} onChange={(e) => setAdjReason(e.target.value)} placeholder="ör. Ay içinde üstün performans / eksik sayım" className="mt-1" rows={2} />
            </div>
            <Button data-testid="adjust-submit" onClick={submitAdjust} disabled={saving} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Uygula
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reward create/edit dialog */}
      <Dialog open={rwOpen} onOpenChange={setRwOpen}>
        <DialogContent data-testid="reward-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{rwEditing ? "Ödülü Düzenle" : "Yeni Ödül"}</DialogTitle>
            <DialogDescription>Puana göre verilebilecek ödülleri tanımlayın.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Ödül Adı</Label><Input data-testid="reward-name" value={rwForm.name} onChange={(e) => setRwForm({ ...rwForm, name: e.target.value })} className="mt-1" placeholder="ör. Ayın Personeli · Hediye Çeki" /></div>
            <div><Label>Açıklama</Label><Input data-testid="reward-desc" value={rwForm.description} onChange={(e) => setRwForm({ ...rwForm, description: e.target.value })} className="mt-1" placeholder="Kısa açıklama" /></div>
            <div><Label>Gerekli Puan</Label><Input data-testid="reward-points" type="number" value={rwForm.points_required} onChange={(e) => setRwForm({ ...rwForm, points_required: e.target.value })} className="mt-1 tabular" /></div>
            <div className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2">
              <Label className="mb-0">Aktif</Label>
              <Switch data-testid="reward-active" checked={rwForm.active} onCheckedChange={(v) => setRwForm({ ...rwForm, active: v })} />
            </div>
            <Button data-testid="reward-submit" onClick={submitReward} disabled={saving} className="w-full bg-brand-orange text-white hover:bg-orange-600">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {rwEditing ? "Kaydet" : "Ekle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Grant dialog */}
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent data-testid="reward-grant-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Ödül Ver · {grantReward?.name}</DialogTitle>
            <DialogDescription>Bu ödülü bir personele verin. Kişi ve yönetim bilgilendirilir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Personel</Label>
              <select data-testid="grant-user" value={grantUser} onChange={(e) => setGrantUser(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                <option value="">Seçin</option>
                {board.map((b) => <option key={b.user_email} value={b.user_email}>{b.name} · {b.score} puan ({b.role_label})</option>)}
              </select>
            </div>
            <div><Label>Not (opsiyonel)</Label><Input data-testid="grant-note" value={grantNote} onChange={(e) => setGrantNote(e.target.value)} className="mt-1" placeholder="ör. Haziran ayı en yüksek performans" /></div>
            <Button data-testid="grant-submit" onClick={submitGrant} disabled={saving} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Ödülü Ver
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scoring automation rules dialog */}
      <Dialog open={rulesOpen} onOpenChange={setRulesOpen}>
        <DialogContent data-testid="scoring-rules-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Puan Otomasyon Kuralları</DialogTitle>
            <DialogDescription>Olaylara göre puanı otomatik +/- yapan kuralları tanımlayın. Kapalıysa yalnız fire/zayiat ve manuel puanlar işler.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold text-slate-800"><Clock className="h-4 w-4 text-amber-500" /> Geç kasa açılışı</span>
                <Switch data-testid="rule-late-enabled" checked={rulesForm.late_shift_enabled} onCheckedChange={(v) => setRulesForm({ ...rulesForm, late_shift_enabled: v })} />
              </div>
              {rulesForm.late_shift_enabled && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Son açılış saati (SS:DD)</Label><Input data-testid="rule-late-deadline" value={rulesForm.shift_open_deadline} onChange={(e) => setRulesForm({ ...rulesForm, shift_open_deadline: e.target.value })} className="mt-1 h-9" placeholder="09:15" /></div>
                  <div><Label className="text-[11px]">Ceza puanı (−)</Label><Input data-testid="rule-late-penalty" type="number" min="0" value={rulesForm.late_shift_penalty} onChange={(e) => setRulesForm({ ...rulesForm, late_shift_penalty: e.target.value })} className="mt-1 h-9 tabular" /></div>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold text-slate-800"><AlertTriangle className="h-4 w-4 text-red-500" /> Teslim/sayım tutarsızlığı</span>
                <Switch data-testid="rule-disc-enabled" checked={rulesForm.discrepancy_enabled} onCheckedChange={(v) => setRulesForm({ ...rulesForm, discrepancy_enabled: v })} />
              </div>
              {rulesForm.discrepancy_enabled && (
                <div className="mt-2"><Label className="text-[11px]">Ceza puanı (−)</Label><Input data-testid="rule-disc-penalty" type="number" min="0" value={rulesForm.discrepancy_penalty} onChange={(e) => setRulesForm({ ...rulesForm, discrepancy_penalty: e.target.value })} className="mt-1 h-9 w-32 tabular" /></div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold text-slate-800"><Target className="h-4 w-4 text-emerald-600" /> Günlük satış hedefi aşımı</span>
                <Switch data-testid="rule-target-enabled" checked={rulesForm.target_enabled} onCheckedChange={(v) => setRulesForm({ ...rulesForm, target_enabled: v })} />
              </div>
              {rulesForm.target_enabled && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Günlük hedef ({curSym()})</Label><Input data-testid="rule-target-amount" type="number" min="0" value={rulesForm.daily_sales_target} onChange={(e) => setRulesForm({ ...rulesForm, daily_sales_target: e.target.value })} className="mt-1 h-9 tabular" /></div>
                  <div><Label className="text-[11px]">Ödül puanı (+)</Label><Input data-testid="rule-target-bonus" type="number" min="0" value={rulesForm.target_bonus} onChange={(e) => setRulesForm({ ...rulesForm, target_bonus: e.target.value })} className="mt-1 h-9 tabular" /></div>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold text-slate-800"><Star className="h-4 w-4 text-amber-500" /> Kasa sayım doğruluğu (Z-rapor ödülü)</span>
                <Switch data-testid="rule-cashreward-enabled" checked={rulesForm.cash_reward_enabled} onCheckedChange={(v) => setRulesForm({ ...rulesForm, cash_reward_enabled: v })} />
              </div>
              {rulesForm.cash_reward_enabled && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Kasa DENK ise ödül (+)</Label><Input data-testid="rule-cashreward-exact" type="number" value={rulesForm.cash_reward_exact} onChange={(e) => setRulesForm({ ...rulesForm, cash_reward_exact: e.target.value })} className="mt-1 h-9 tabular" /></div>
                  <div><Label className="text-[11px]">Kasa FAZLA ise ödül (+)</Label><Input data-testid="rule-cashreward-over" type="number" value={rulesForm.cash_reward_over} onChange={(e) => setRulesForm({ ...rulesForm, cash_reward_over: e.target.value })} className="mt-1 h-9 tabular" /></div>
                  <div><Label className="text-[11px]">Kasa EKSİK ise puan (± / 0)</Label><Input data-testid="rule-cashreward-below" type="number" value={rulesForm.cash_below_points} onChange={(e) => setRulesForm({ ...rulesForm, cash_below_points: e.target.value })} className="mt-1 h-9 tabular" /></div>
                  <div className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1.5">
                    <Label className="mb-0 text-[11px]">Satış yoksa ödül yok</Label>
                    <Switch data-testid="rule-cashreward-activity" checked={rulesForm.cash_require_activity} onCheckedChange={(v) => setRulesForm({ ...rulesForm, cash_require_activity: v })} />
                  </div>
                  <div className="col-span-2 mt-1 grid grid-cols-3 gap-2 border-t border-slate-100 pt-2">
                    <div><Label className="text-[11px]">Min. açık saat</Label><Input data-testid="rule-cashreward-minhours" type="number" min="0" step="0.5" value={rulesForm.cash_min_open_hours} onChange={(e) => setRulesForm({ ...rulesForm, cash_min_open_hours: e.target.value })} className="mt-1 h-9 tabular" placeholder="ör. 3" /></div>
                    <div><Label className="text-[11px]">Min. satış adedi</Label><Input data-testid="rule-cashreward-mincount" type="number" min="0" value={rulesForm.cash_min_sales_count} onChange={(e) => setRulesForm({ ...rulesForm, cash_min_sales_count: e.target.value })} className="mt-1 h-9 tabular" placeholder="ör. 20" /></div>
                    <div><Label className="text-[11px]">Min. ciro ({curSym()})</Label><Input data-testid="rule-cashreward-minamount" type="number" min="0" value={rulesForm.cash_min_sales_amount} onChange={(e) => setRulesForm({ ...rulesForm, cash_min_sales_amount: e.target.value })} className="mt-1 h-9 tabular" placeholder="ör. 1000" /></div>
                  </div>
                  <p className="col-span-2 text-[10px] text-slate-400">Anti-cheat: ödül yalnızca kasa <b>en az</b> girilen süre açık kaldıysa <b>ve</b> <b>en az</b> girilen satış/ciro yapıldıysa verilir (0 = koşul yok). Örn. "3 saat açık <b>VE</b> 20 satış" → ödül. Kasa eksikse yukarıdaki puan işlenir; koşullar sağlanmazsa ödül verilmez. Kasiyer hedefi tutturursa Z-rapora ★ yıldızla yazdırılır.</p>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold text-slate-800"><Target className="h-4 w-4 text-emerald-600" /> Yüksek müşteri anketi (lokasyon ödülü)</span>
                <Switch data-testid="rule-praise-enabled" checked={rulesForm.survey_praise_enabled} onCheckedChange={(v) => setRulesForm({ ...rulesForm, survey_praise_enabled: v })} />
              </div>
              {rulesForm.survey_praise_enabled && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Eşik puan (≥)</Label><Input data-testid="rule-praise-min" type="number" min="1" max="5" value={rulesForm.survey_praise_min} onChange={(e) => setRulesForm({ ...rulesForm, survey_praise_min: e.target.value })} className="mt-1 h-9 tabular" /></div>
                  <div><Label className="text-[11px]">Ödül puanı (+)</Label><Input data-testid="rule-praise-bonus" type="number" min="0" value={rulesForm.survey_praise_bonus} onChange={(e) => setRulesForm({ ...rulesForm, survey_praise_bonus: e.target.value })} className="mt-1 h-9 tabular" /></div>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold text-slate-800"><AlertTriangle className="h-4 w-4 text-red-500" /> Düşük müşteri anketi / şikayet (lokasyon cezası)</span>
                <Switch data-testid="rule-surveylow-enabled" checked={rulesForm.survey_low_enabled} onCheckedChange={(v) => setRulesForm({ ...rulesForm, survey_low_enabled: v })} />
              </div>
              {rulesForm.survey_low_enabled && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Eşik puan (≤)</Label><Input data-testid="rule-surveylow-max" type="number" min="1" max="5" value={rulesForm.survey_low_max} onChange={(e) => setRulesForm({ ...rulesForm, survey_low_max: e.target.value })} className="mt-1 h-9 tabular" /></div>
                  <div><Label className="text-[11px]">Ceza puanı (−)</Label><Input data-testid="rule-surveylow-penalty" type="number" min="0" value={rulesForm.survey_low_penalty} onChange={(e) => setRulesForm({ ...rulesForm, survey_low_penalty: e.target.value })} className="mt-1 h-9 tabular" /></div>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold text-slate-800"><Target className="h-4 w-4 text-emerald-600" /> Temiz / eksiksiz mal kabulü (lokasyon ödülü)</span>
                <Switch data-testid="rule-clean-enabled" checked={rulesForm.receipt_clean_enabled} onCheckedChange={(v) => setRulesForm({ ...rulesForm, receipt_clean_enabled: v })} />
              </div>
              {rulesForm.receipt_clean_enabled && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Ödül puanı (+)</Label><Input data-testid="rule-clean-bonus" type="number" min="0" value={rulesForm.receipt_clean_bonus} onChange={(e) => setRulesForm({ ...rulesForm, receipt_clean_bonus: e.target.value })} className="mt-1 h-9 tabular" /></div>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold text-slate-800"><AlertTriangle className="h-4 w-4 text-red-500" /> Mal kabul düzeltme talebi (lokasyon cezası)</span>
                <Switch data-testid="rule-rcorr-enabled" checked={rulesForm.receipt_correction_enabled} onCheckedChange={(v) => setRulesForm({ ...rulesForm, receipt_correction_enabled: v })} />
              </div>
              {rulesForm.receipt_correction_enabled && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Ceza puanı (−)</Label><Input data-testid="rule-rcorr-penalty" type="number" min="0" value={rulesForm.receipt_correction_penalty} onChange={(e) => setRulesForm({ ...rulesForm, receipt_correction_penalty: e.target.value })} className="mt-1 h-9 tabular" /></div>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-semibold text-slate-800"><Star className="h-4 w-4 text-emerald-600" /> Günlük müşteri memnuniyeti (benzersiz müşteri → lokasyon ödülü)</span>
                <Switch data-testid="rule-survloc-enabled" checked={rulesForm.survey_location_enabled} onCheckedChange={(v) => setRulesForm({ ...rulesForm, survey_location_enabled: v })} />
              </div>
              {rulesForm.survey_location_enabled && (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <div><Label className="text-[11px]">Min. farklı müşteri/gün</Label><Input data-testid="rule-survloc-unique" type="number" min="1" value={rulesForm.survey_location_min_unique} onChange={(e) => setRulesForm({ ...rulesForm, survey_location_min_unique: e.target.value })} className="mt-1 h-9 tabular" /></div>
                  <div><Label className="text-[11px]">Min. ortalama (≥)</Label><Input data-testid="rule-survloc-avg" type="number" min="1" max="5" step="0.1" value={rulesForm.survey_location_min_avg} onChange={(e) => setRulesForm({ ...rulesForm, survey_location_min_avg: e.target.value })} className="mt-1 h-9 tabular" /></div>
                  <div><Label className="text-[11px]">Ödül puanı (+)</Label><Input data-testid="rule-survloc-bonus" type="number" min="0" value={rulesForm.survey_location_bonus} onChange={(e) => setRulesForm({ ...rulesForm, survey_location_bonus: e.target.value })} className="mt-1 h-9 tabular" /></div>
                  <p className="col-span-3 text-[10px] text-slate-400">Bir lokasyon gün içinde en az bu kadar <b>farklı</b> müşteriden anket alır ve ortalaması eşiği geçerse, o güne <b>bir kez</b> lokasyon puanı eklenir (aynı müşteri tekrar sayılmaz).</p>
                </div>
              )}
            </div>
            <Button data-testid="rules-submit" onClick={submitRules} disabled={saving} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Kuralları Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
